import React, { useRef, useEffect, useState } from "react";
import {
  ArrowUp,
  ChevronDown,
  Sparkles,
  X,
  Check,
  Wand2,
  Palette,
  Languages,
  ListPlus,
  GitBranch,
  Image as ImageIcon,
  Undo2,
} from "lucide-react";
import SpeechToText from "../SpeechToText";
import { useTypewriter } from "../../hooks/useTypewriter";

const AgentAvatar = ({ size = 24 }) => (
  <img
    src="/images/pollBoltIconCyan.svg"
    alt=""
    className="rounded-full flex-shrink-0"
    style={{ width: size, height: size }}
  />
);

const BRAND_TINT = "from-brand-primary to-brand-primary-hover";

const ACTION_META = {
  modify_design: { icon: Palette, label: "Updated the design", tint: BRAND_TINT },
  add_questions: { icon: ListPlus, label: "Added questions", tint: BRAND_TINT },
  translate: { icon: Languages, label: "Translated the survey", tint: BRAND_TINT },
  add_image: { icon: ImageIcon, label: "Added imagery", tint: BRAND_TINT },
  generate_image: { icon: Wand2, label: "Generated a custom image", tint: BRAND_TINT },
  update_logic: { icon: GitBranch, label: "Updated the logic", tint: BRAND_TINT },
};

const ToolCard = ({ metadata }) => {
  if (!metadata?.action) return null;
  const meta = ACTION_META[metadata.action] || {
    icon: Check,
    label: "Done",
    tint: "from-neutral-400 to-neutral-500",
  };
  const Icon = meta.icon;
  const label = metadata.summary || meta.label;

  return (
    <div className="inline-flex items-center gap-2 mt-1.5 pl-1.5 pr-3 py-1 rounded-full bg-white border border-neutral-200 shadow-sm">
      <span
        className={`w-5 h-5 rounded-full bg-gradient-to-br ${meta.tint} flex items-center justify-center flex-shrink-0`}
      >
        <Icon size={11} className="text-white" strokeWidth={2.5} />
      </span>
      <span className="text-[12px] font-medium text-neutral-700">{label}</span>
    </div>
  );
};

const IMAGE_LOADING_STEPS = {
  en: [
    "Imagining the composition…",
    "Sketching the scene…",
    "Painting in the details…",
    "Adding light & color…",
    "Polishing the final image…",
  ],
  he: [
    "מדמיין את ההרכב…",
    "משרטט את הסצנה…",
    "מצייר את הפרטים…",
    "מוסיף אור וצבע…",
    "מלטש את התמונה הסופית…",
  ],
};

const ImageGenLoader = ({ hebrew }) => {
  const steps = hebrew ? IMAGE_LOADING_STEPS.he : IMAGE_LOADING_STEPS.en;
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIdx((i) => (i + 1) % steps.length);
    }, 2000);
    return () => clearInterval(timer);
  }, [steps.length]);

  const font = hebrew ? { fontFamily: "'Noto Sans Hebrew', sans-serif" } : undefined;

  return (
    <div className="mt-1 w-full max-w-[260px]">
      <div className="relative h-28 w-full rounded-xl overflow-hidden bg-gradient-to-r from-neutral-100 via-neutral-200 to-neutral-100 bg-[length:200%_100%] animate-shimmer">
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="w-10 h-10 rounded-full bg-white/70 backdrop-blur-sm flex items-center justify-center shadow-sm">
            <Wand2 size={17} className="text-brand-primary animate-pulse" />
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2" dir="auto" style={font}>
        <Sparkles size={13} className="text-neutral-400 animate-pulse flex-shrink-0" />
        <span key={idx} className="text-[12.5px] text-neutral-500 animate-fadeIn">
          {steps[idx]}
        </span>
      </div>
    </div>
  );
};

const TypewriterMessage = ({ content, isStreaming }) => {
  const { displayedText, isTyping } = useTypewriter(content, {
    enabled: isStreaming,
    granularity: "char",
    delayMs: 8,
    maxBuffer: 2000,
  });
  return (
    <>
      {isStreaming ? displayedText : content}
      {isTyping && <span className="typewriter-cursor" />}
    </>
  );
};

const ReasoningSteps = ({ steps = [], activeIndex = 0 }) => {
  if (!steps.length) return null;
  return (
    <div className="flex items-start gap-2 py-1 mb-1">
      <Sparkles size={14} className="flex-shrink-0 mt-0.5 text-neutral-400 animate-pulse" />
      <div className="flex-1 min-w-0">
        {steps.map((step, i) => (
          <div
            key={i}
            className={`reasoning-bullet text-[12px] leading-relaxed transition-opacity duration-300 ${
              i <= activeIndex ? "text-neutral-500" : "text-neutral-300"
            }`}
            style={{ animationDelay: `${i * 150}ms` }}
          >
            {step}
          </div>
        ))}
      </div>
    </div>
  );
};

const LAYOUT_OPTIONS = [
  { id: "left", label: "Left side", icon: "◧" },
  { id: "right", label: "Right side", icon: "◨" },
  { id: "cover", label: "Full background", icon: "▣" },
];

const hasHebrew = (text) => /[\u0590-\u05FF]/.test(text || "");
const hebrewFontStyle = (text) =>
  hasHebrew(text) ? { fontFamily: "'Noto Sans Hebrew', sans-serif" } : undefined;

const ACCENT = "#F2B600";

const DEFAULT_ROTATING_PLACEHOLDERS = [
  'Try: "make the buttons rounder"',
  'Try: "add a rating question"',
  'Try: "change the background to navy"',
  'Try: "translate to Hebrew"',
];

const AgentDock = ({
  messages = [],
  value = "",
  onChange,
  onSubmit,
  isLoading = false,
  isExpanded = false,
  onExpandedChange,
  leftTools,
  placeholderCollapsed = "Ask me anything...",
  placeholderExpanded = "Type your message...",
  isMobile = false,
  containerClassName = "",
  reasoningSteps = [],
  images = [],
  onRemoveImage,
  onChipClick,
  onLayoutSelect,
  rotatingPlaceholders = DEFAULT_ROTATING_PLACEHOLDERS,
  editTargetLabel = null,
  onClearEditTarget,
  onUndoAction,
  canUndo = false,
  mentionables = [],
}) => {
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [mentionQuery, setMentionQuery] = useState(null);

  // "@" element picker: detect an @token being typed right before the caret.
  const handleInputChange = (e) => {
    const val = e.target.value;
    onChange?.(val);
    const pos = e.target.selectionStart ?? val.length;
    const m = val.slice(0, pos).match(/(?:^|\s)@([\w֐-׿]*)$/);
    setMentionQuery(m && mentionables.length ? m[1].toLowerCase() : null);
  };

  const insertMention = (item) => {
    const el = inputRef.current;
    const pos = el?.selectionStart ?? (value || "").length;
    const before = (value || "").slice(0, pos).replace(/@([\w֐-׿]*)$/, `${item.phrase} `);
    const after = (value || "").slice(pos);
    onChange?.(before + after);
    setMentionQuery(null);
    setTimeout(() => {
      if (el) { el.focus(); el.setSelectionRange(before.length, before.length); }
    }, 0);
  };

  const mentionMatches =
    mentionQuery === null
      ? []
      : mentionables.filter((m) => m.label.toLowerCase().includes(mentionQuery)).slice(0, 6);

  useEffect(() => {
    if (isExpanded && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isExpanded]);

  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  useEffect(() => {
    if (!isExpanded) return;
    const handler = (e) => {
      if (e.key === "Escape") onExpandedChange?.(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isExpanded, onExpandedChange]);

  // Cycle through reasoning steps while loading
  useEffect(() => {
    if (!isLoading || !reasoningSteps.length) {
      setActiveStepIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setActiveStepIndex((prev) =>
        prev < reasoningSteps.length - 1 ? prev + 1 : prev
      );
    }, 1500);
    return () => clearInterval(interval);
  }, [isLoading, reasoningSteps]);

  const handleKeyDown = (e) => {
    if (e.key === "Escape" && mentionQuery !== null) {
      e.preventDefault();
      e.stopPropagation();
      setMentionQuery(null);
      return;
    }
    if (e.key === "Enter" && !e.shiftKey && !isLoading) {
      if (mentionMatches.length) { e.preventDefault(); insertMention(mentionMatches[0]); return; }
      e.preventDefault();
      onSubmit?.();
    }
  };

  const hasHistory = messages.length > 0;

  // Rotate example placeholders while the bar is idle (collapsed & empty)
  useEffect(() => {
    if (isExpanded || (value || "").trim() || !rotatingPlaceholders.length) return;
    const t = setInterval(() => {
      setPlaceholderIndex((i) => (i + 1) % rotatingPlaceholders.length);
    }, 3000);
    return () => clearInterval(t);
  }, [isExpanded, value, rotatingPlaceholders.length]);

  const collapsedPlaceholder =
    rotatingPlaceholders.length && !(value || "").trim()
      ? rotatingPlaceholders[placeholderIndex % rotatingPlaceholders.length]
      : placeholderCollapsed;
  const inputPlaceholder = editTargetLabel
    ? "Tell AI what to change…"
    : isExpanded
    ? placeholderExpanded
    : collapsedPlaceholder;

  return (
    <div className={`flex flex-col items-center ${containerClassName}`}>
      {isExpanded && (
        <>
          <div
            className="fixed inset-0 bg-black/10 z-[59]"
            onClick={() => onExpandedChange?.(false)}
          />

          <div
            className={`relative z-[60] w-full bg-white rounded-2xl shadow-2xl border border-neutral-200 mb-2 flex flex-col overflow-hidden ${
              isMobile ? "max-h-[70vh]" : "max-h-[50vh]"
            }`}
            style={{ maxWidth: 520 }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100 flex-shrink-0">
              <div className="flex items-center gap-2">
                <AgentAvatar size={22} />
                <span className="text-[14px] font-semibold text-neutral-800">
                  PollBolt AI
                </span>
              </div>
              <button
                onClick={() => onExpandedChange?.(false)}
                className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-full transition-colors"
              >
                <ChevronDown size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
              {messages.length === 0 && (
                <div className="text-center py-8 text-neutral-400 text-[13px]">
                  No conversation yet. Send a message to get started.
                </div>
              )}
              {messages.map((msg, i) => {
                if (msg.role === "user") {
                  return (
                    <div
                      key={msg.id || `msg-${i}`}
                      className="flex flex-col items-end"
                    >
                      <div className="bg-neutral-100 rounded-2xl px-4 py-2.5 max-w-[80%]">
                        <p className="text-[13px] text-neutral-900 leading-relaxed whitespace-pre-wrap" dir="auto" style={hebrewFontStyle(msg.content)}>
                          {msg.content}
                        </p>
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={msg.id || `msg-${i}`}
                    className="flex items-start gap-2.5"
                  >
                    <div className="mt-0.5 flex-shrink-0">
                      <AgentAvatar size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                      {msg.metadata?.generating ? (
                        <ImageGenLoader hebrew={hasHebrew(msg.content)} />
                      ) : (
                        <p className="text-[13px] text-neutral-800 leading-relaxed whitespace-pre-wrap" dir="auto" style={hebrewFontStyle(msg.content)}>
                          <TypewriterMessage content={msg.content} isStreaming={!!msg.isStreaming} />
                        </p>
                      )}
                      {msg.metadata?.imageUrl && (
                        <div className="mt-2 rounded-xl overflow-hidden border border-neutral-200 max-w-[220px]">
                          <img src={msg.metadata.imageUrl} alt="" className="w-full h-28 object-cover" />
                        </div>
                      )}
                      {msg.metadata && <ToolCard metadata={msg.metadata} />}
                      {i === messages.length - 1 &&
                        canUndo &&
                        msg.metadata?.action &&
                        !["clarify", "generate_image"].includes(msg.metadata.action) && (
                          <button
                            type="button"
                            onClick={() => onUndoAction?.()}
                            className="inline-flex items-center gap-1.5 mt-1.5 ml-2 px-2.5 py-1 rounded-full text-[12px] font-medium text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100 transition-colors"
                            title="Undo this change"
                          >
                            <Undo2 size={13} />
                            Undo
                          </button>
                        )}
                      {Array.isArray(msg.metadata?.chips) && msg.metadata.chips.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {msg.metadata.chips.map((chip, j) => (
                            <button
                              key={j}
                              onClick={() => onChipClick?.(chip)}
                              dir="auto"
                              style={hebrewFontStyle(chip)}
                              className="text-[12px] px-3 py-1.5 rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50 active:bg-neutral-100 transition-colors"
                            >
                              {chip}
                            </button>
                          ))}
                        </div>
                      )}
                      {msg.metadata?.layoutPicker && (
                        <div className="flex gap-2 mt-2">
                          {(Array.isArray(msg.metadata?.layoutOptions) && msg.metadata.layoutOptions.length
                            ? msg.metadata.layoutOptions
                            : LAYOUT_OPTIONS
                          ).map((opt) => (
                            <button
                              key={opt.id}
                              onClick={() => onLayoutSelect?.(opt.id)}
                              className="flex flex-col items-center gap-1 px-4 py-3 rounded-xl border border-neutral-200 text-neutral-600 hover:bg-neutral-50 hover:border-neutral-300 active:bg-neutral-100 transition-colors flex-1"
                            >
                              <span className="text-xl">{opt.icon}</span>
                              <span className="text-[11px] font-medium" style={hebrewFontStyle(opt.label)}>{opt.label}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {isLoading && messages[messages.length - 1]?.role !== "assistant" && (
                <div className="flex items-start gap-2.5">
                  <AgentAvatar size={24} />
                  <div className="flex-1 min-w-0">
                    {reasoningSteps.length > 0 ? (
                      <ReasoningSteps steps={reasoningSteps} activeIndex={activeStepIndex} />
                    ) : (
                      <span className="text-[13px] font-medium thinking-shimmer">
                        Thinking
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>
        </>
      )}

      <div
        className={`w-full relative bg-white shadow-2xl hover:shadow-xl rounded-2xl ${
          isExpanded ? "z-[60] border border-gray-200" : "border-[1.5px]"
        } py-3`}
        style={isExpanded ? undefined : { borderColor: ACCENT }}
      >
        {mentionMatches.length > 0 && (
          <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-[70] max-h-56 overflow-y-auto">
            {mentionMatches.map((item) => (
              <button
                key={item.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); insertMention(item); }}
                dir="auto"
                className="w-full text-left px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <span style={{ color: ACCENT }}>@</span>
                {item.label}
              </button>
            ))}
          </div>
        )}
        {images.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap px-4 pb-2">
            {images.map((image, index) => (
              <div key={image.id} className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg pl-1 pr-2 py-1">
                <div className="w-6 h-6 rounded overflow-hidden flex-shrink-0">
                  <img
                    src={image.thumbnailUrl || image.imageUrl}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-xs text-gray-600 max-w-[100px] truncate">{image.name || `image ${image.id?.slice(-4) || index + 1}`}</span>
                <button
                  onClick={() => onRemoveImage?.(index)}
                  className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-200"
                >
                  <X size={10} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-3 px-4">
          {!isExpanded && (
            <Sparkles size={18} className="flex-shrink-0" style={{ color: ACCENT }} aria-hidden="true" />
          )}
          {leftTools}

          {editTargetLabel && (
            <span
              className="flex items-center gap-1 text-[12px] font-medium px-2.5 py-1 rounded-full flex-shrink-0"
              style={{ background: "#FAEEDA", color: "#854F0B" }}
            >
              {editTargetLabel}
              <button
                type="button"
                onClick={onClearEditTarget}
                className="ml-0.5 hover:opacity-70"
                aria-label="Clear edit target"
              >
                <X size={12} />
              </button>
            </span>
          )}

          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (!isExpanded && hasHistory) onExpandedChange?.(true);
            }}
            placeholder={inputPlaceholder}
            className="flex-1 min-w-0 outline-none text-base md:text-sm text-gray-700 placeholder-gray-400 bg-transparent"
            dir="auto"
            style={hebrewFontStyle(value)}
            disabled={isLoading}
          />

          <SpeechToText
            onTranscription={(text) => {
              const cur = value || "";
              onChange?.(cur ? `${cur} ${text}` : text);
            }}
            disabled={isLoading}
            language="en"
            size="w-5 h-5"
            className="p-2 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-colors"
          />

          {!isExpanded && hasHistory && (
            <button
              onClick={() => onExpandedChange?.(true)}
              className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-full transition-colors"
              title="View conversation"
            >
              <ChevronDown size={16} className="rotate-180" />
            </button>
          )}

          {isLoading ? (
            <div className="p-2.5 bg-black rounded-full flex-shrink-0">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <button
              onClick={onSubmit}
              disabled={!value.trim()}
              className="flex-shrink-0 p-2.5 bg-black text-white rounded-full hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Submit"
            >
              <ArrowUp size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentDock;
